import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { LOGO_URL } from "../lib/site";

/**
 * Cinematic Palace-Door entry — "the doors open and the title is revealed".
 *
 * Phases:
 *   closed   (1.6s)  — Two ornate carved wooden palace doors, sealed shut.
 *                      Brass studs, gold arch, gold filigree, drifting gold dust.
 *   opening  (3.2s)  — Doors slowly part outward; warm gold light beam grows
 *                      from the seam; AB EVENTS title rises behind them.
 *   revealed (1.6s)  — Doors fully off-screen; tagline + hairline appear.
 *   done             — Loader unmounts, hero is shown.
 */

const PHASES = {
  CLOSED: 1600,
  OPENING: 3200,
  REVEALED: 1600,
};

const getIntroProfile = () => {
  if (typeof window === "undefined") {
    return { ...PHASES, dustCount: 16, lite: false };
  }

  const reducedMotion =
    window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false;
  const memory = navigator.deviceMemory ?? 8;
  const cores = navigator.hardwareConcurrency ?? 8;
  const smallViewport = window.innerWidth < 1180;
  const lite = reducedMotion || memory <= 6 || cores <= 6 || smallViewport;

  if (!lite) {
    return { ...PHASES, dustCount: 16, lite: false };
  }

  return {
    CLOSED: 800,
    OPENING: 1700,
    REVEALED: 500,
    dustCount: 6,
    lite: true,
  };
};

/* ------------------------------------------------------------------ */
/* CSS for one ornate palace-door half. Pure CSS, no external assets.  */
/* ------------------------------------------------------------------ */
const doorBase = {
  // Layered: subtle vertical wood grain + warm walnut gradient
  backgroundImage: [
    // Brass stud grid (small radial dots)
    "radial-gradient(circle at 50% 50%, rgba(212,175,55,0.85) 0 2px, rgba(212,175,55,0) 3px)",
    // Carved bead-line vertical strokes
    "repeating-linear-gradient(90deg, rgba(0,0,0,0.18) 0 1px, rgba(0,0,0,0) 1px 8px)",
    // Wood grain
    "repeating-linear-gradient(180deg, rgba(0,0,0,0.10) 0 1px, rgba(255,255,255,0.02) 1px 3px)",
    // Walnut/mahogany base
    "radial-gradient(ellipse at 50% 35%, #5a2c12 0%, #3b1a08 45%, #1f0d04 100%)",
  ].join(", "),
  backgroundSize: "44px 44px, auto, auto, 100% 100%",
  backgroundPosition: "center, center, center, center",
  boxShadow:
    "inset 0 0 80px rgba(0,0,0,0.7), inset 0 0 200px rgba(0,0,0,0.45)",
};

/* SVG ornate filigree centerpiece, gold outline only */
const Filigree = ({ side }) => (
  <svg
    viewBox="0 0 200 360"
    className={`absolute top-1/2 -translate-y-1/2 ${
      side === "left" ? "right-6" : "left-6"
    } w-[28%] max-w-[260px] h-auto opacity-80`}
    aria-hidden="true"
  >
    <g
      fill="none"
      stroke="rgba(212,175,55,0.85)"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ filter: "drop-shadow(0 0 6px rgba(212,175,55,0.45))" }}
    >
      {/* Top arch */}
      <path d="M30 60 Q100 0 170 60" />
      <path d="M40 70 Q100 18 160 70" />
      {/* Inner cartouche */}
      <path d="M50 80 L150 80 L150 280 Q100 320 50 280 Z" />
      <path d="M58 92 L142 92 L142 272 Q100 308 58 272 Z" />
      {/* Diamond + leaves */}
      <path d="M100 120 L120 150 L100 180 L80 150 Z" />
      <path d="M100 200 Q90 215 100 230 Q110 215 100 200" />
      <path d="M100 240 L100 270" />
      {/* Side curls */}
      <path d="M50 130 Q30 145 50 165" />
      <path d="M150 130 Q170 145 150 165" />
      <path d="M50 200 Q30 215 50 235" />
      <path d="M150 200 Q170 215 150 235" />
      {/* Crown dot */}
      <circle cx="100" cy="34" r="2.4" fill="rgba(212,175,55,0.9)" />
      {/* Hairlines */}
      <path d="M70 96 L70 268" />
      <path d="M130 96 L130 268" />
    </g>
  </svg>
);

export const IntroLoader = ({ onDone, onOpening, onReveal }) => {
  const [profile] = useState(getIntroProfile);
  const [phase, setPhase] = useState("closed");
  const onDoneRef = useRef(onDone);
  const onOpeningRef = useRef(onOpening);
  const onRevealRef = useRef(onReveal);
  const completedRef = useRef(false);

  useEffect(() => {
    onDoneRef.current = onDone;
  }, [onDone]);

  useEffect(() => {
    onOpeningRef.current = onOpening;
  }, [onOpening]);

  useEffect(() => {
    onRevealRef.current = onReveal;
  }, [onReveal]);

  useEffect(() => {
    const t1 = setTimeout(() => {
      setPhase("opening");
      onOpeningRef.current && onOpeningRef.current();
    }, profile.CLOSED);
    const t2 = setTimeout(() => {
      setPhase("revealed");
      onRevealRef.current && onRevealRef.current();
    }, profile.CLOSED + profile.OPENING);
    const t3 = setTimeout(() => {
      if (completedRef.current) return;
      completedRef.current = true;
      setPhase("done");
      onDoneRef.current && onDoneRef.current();
    }, profile.CLOSED + profile.OPENING + profile.REVEALED);

    document.body.style.overflow = "hidden";
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      document.body.style.overflow = "";
    };
  }, [profile]);

  useEffect(() => {
    if (phase === "done") document.body.style.overflow = "";
  }, [phase]);

  const opening = phase === "opening" || phase === "revealed";
  const revealed = phase === "revealed";

  return (
    <AnimatePresence>
      {phase !== "done" && (
        <motion.div
          data-testid="intro-loader"
          key="palace-door-intro"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-0 z-[300] overflow-hidden bg-[#050201]"
        >
          {/* === Layer 0 — Backdrop visible behind opening doors === */}
          <div className="absolute inset-0">
            <div
              className="absolute inset-0"
              style={{
                background:
                  "radial-gradient(ellipse at center, #1a0a08 0%, #0c0604 55%, #050201 100%)",
              }}
            />
            <motion.div
              className="absolute left-[-10%] top-[18%] h-[48vh] w-[30vw] max-w-[420px] rounded-full pointer-events-none"
              animate={{
                opacity: opening ? 0.2 : 0.08,
                scale: opening ? 1.06 : 1,
              }}
              transition={{ duration: 2.6, ease: [0.22, 1, 0.36, 1] }}
              style={{
                background:
                  "radial-gradient(ellipse at center, rgba(255,218,128,0.18) 0%, rgba(255,218,128,0.05) 32%, rgba(0,0,0,0) 76%)",
                filter: "blur(28px)",
              }}
            />
            <motion.div
              className="absolute right-[-10%] top-[18%] h-[48vh] w-[30vw] max-w-[420px] rounded-full pointer-events-none"
              animate={{
                opacity: opening ? 0.2 : 0.08,
                scale: opening ? 1.06 : 1,
              }}
              transition={{ duration: 2.6, ease: [0.22, 1, 0.36, 1] }}
              style={{
                background:
                  "radial-gradient(ellipse at center, rgba(255,218,128,0.18) 0%, rgba(255,218,128,0.05) 32%, rgba(0,0,0,0) 76%)",
                filter: "blur(28px)",
              }}
            />
            <div
              className="absolute inset-0 opacity-[0.06] mix-blend-overlay"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 1px 1px, #d4af37 1px, transparent 0)",
                backgroundSize: "32px 32px",
              }}
            />
            {/* Warm gold beam emerging through the seam */}
            <motion.div
              initial={{ opacity: 0, scaleY: 0.5 }}
              animate={{
                opacity: opening ? 1 : 0,
                scaleY: opening ? 1.4 : 0.5,
              }}
              transition={{ duration: 2.8, ease: [0.22, 1, 0.36, 1] }}
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[40vw] h-[140vh] origin-center pointer-events-none"
              style={{
                background:
                  "radial-gradient(ellipse at center, rgba(255,200,90,0.42) 0%, rgba(212,175,55,0.20) 30%, rgba(0,0,0,0) 70%)",
                filter: "blur(40px)",
              }}
            />

            {/* Brand block revealed behind the doors */}
            <div
              className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center"
              style={{ perspective: "1200px" }}
            >
              <motion.div
                initial={{ opacity: 0, y: 18, rotateX: -6, scale: 0.98 }}
                animate={{
                  opacity: opening ? 1 : 0,
                  y: opening ? 0 : 18,
                  rotateX: opening ? 0 : -6,
                  scale: revealed ? 1.01 : 1,
                }}
                transition={{
                  duration: 1.8,
                  delay: 0.45,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className="relative flex flex-col items-center"
              >
                <motion.div
                  className="absolute left-1/2 top-[4%] h-[34vh] w-[50vw] max-w-[620px] -translate-x-1/2 rounded-full"
                  animate={{ opacity: opening ? 0.46 : 0, scale: opening ? 1 : 0.86 }}
                  transition={{ duration: 1.8, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  style={{
                    background:
                      "radial-gradient(ellipse at center, rgba(212,175,55,0.16) 0%, rgba(212,175,55,0.05) 42%, rgba(0,0,0,0) 78%)",
                    filter: "blur(26px)",
                  }}
                />
                <motion.div
                  className="absolute left-1/2 top-[44%] h-[34vh] w-[34vw] max-w-[460px] -translate-x-1/2 rounded-[44px]"
                  animate={{ opacity: opening ? 0.18 : 0, scaleY: opening ? 1 : 0.92 }}
                  transition={{ duration: 1.8, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  style={{
                    background: [
                      "linear-gradient(180deg, rgba(255,230,160,0.08) 0%, rgba(255,230,160,0) 22%)",
                      "linear-gradient(180deg, rgba(55,24,8,0.65) 0%, rgba(18,8,3,0.18) 100%)",
                    ].join(", "),
                    boxShadow: "inset 0 0 22px rgba(255,214,120,0.06)",
                    filter: "blur(0.2px)",
                  }}
                />
                <motion.div
                  className="absolute left-1/2 top-[72%] h-[16vh] w-[44vw] max-w-[620px] -translate-x-1/2 rounded-full"
                  animate={{
                    opacity: revealed ? 0.55 : 0,
                    scaleX: revealed ? 1 : 0.7,
                    scaleY: revealed ? 1 : 0.8,
                  }}
                  transition={{ duration: 1.2, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                  style={{
                    background:
                      "radial-gradient(ellipse at center, rgba(212,175,55,0.18) 0%, rgba(212,175,55,0.08) 32%, rgba(0,0,0,0) 76%)",
                    filter: "blur(20px)",
                  }}
                />
                <motion.div
                  className="absolute left-1/2 top-[74%] h-px w-[38vw] max-w-[520px] -translate-x-1/2"
                  animate={{ opacity: revealed ? 0.65 : 0, scaleX: revealed ? 1 : 0.78 }}
                  transition={{ duration: 1.05, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                  style={{
                    background:
                      "linear-gradient(90deg, rgba(212,175,55,0) 0%, rgba(255,229,150,0.85) 50%, rgba(212,175,55,0) 100%)",
                    boxShadow: "0 0 10px rgba(255,224,136,0.24)",
                  }}
                />

                <div className="relative mb-6 md:mb-8">
                  <motion.div
                    className="absolute inset-[-12%] rounded-full"
                    animate={{
                      opacity: opening ? [0.22, 0.42, 0.22] : 0,
                      scale: opening ? [0.96, 1.02, 0.96] : 0.96,
                    }}
                    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                    style={{
                      background:
                        "radial-gradient(circle at center, rgba(255,224,136,0.2) 0%, rgba(212,175,55,0.06) 48%, rgba(0,0,0,0) 76%)",
                      filter: "blur(16px)",
                    }}
                  />
                  <motion.img
                    src={LOGO_URL}
                    alt="AB Events"
                    initial={{ opacity: 0, y: 20, scale: 0.9 }}
                    animate={{
                      opacity: opening ? 1 : 0,
                      y: opening ? 0 : 20,
                      scale: opening ? 1 : 0.9,
                    }}
                    transition={{
                      duration: 1.6,
                      delay: 0.5,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                    className="relative z-10 h-auto w-28 object-contain drop-shadow-[0_0_60px_rgba(212,175,55,0.7)] sm:w-36 md:w-44 lg:w-52"
                    draggable={false}
                  />
                  <motion.div
                    className="absolute inset-y-[12%] left-[-8%] w-[18%] skew-x-[-20deg]"
                    animate={{
                      x: opening ? ["0%", "620%"] : "0%",
                      opacity: opening ? [0, 0.45, 0] : 0,
                    }}
                    transition={{
                      duration: 1.3,
                      delay: 1.1,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                    style={{
                      background:
                        "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,238,188,0.6) 50%, rgba(255,255,255,0) 100%)",
                      filter: "blur(8px)",
                      mixBlendMode: "screen",
                    }}
                  />
                </div>

                <div className="relative inline-block px-3">
                  <motion.div
                    aria-hidden="true"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: opening ? 0.22 : 0 }}
                    transition={{ duration: 1.2, delay: 0.92 }}
                    className="absolute inset-0 font-serif font-light uppercase text-[36px] sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl leading-none blur-[10px] text-[#d4af37]"
                    style={{ letterSpacing: "0.34em" }}
                  >
                    AB&nbsp;Events
                  </motion.div>
                  <motion.div
                    aria-hidden="true"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: opening ? 0.14 : 0, x: opening ? 6 : 0, y: opening ? 6 : 0 }}
                    transition={{ duration: 1.4, delay: 0.92 }}
                    className="absolute inset-0 font-serif font-light uppercase text-[36px] sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl leading-none text-[#3d1b07]"
                    style={{ letterSpacing: "0.34em" }}
                  >
                    AB&nbsp;Events
                  </motion.div>

                  <motion.h1
                    initial={{ opacity: 0, y: 14, letterSpacing: "0.18em" }}
                    animate={{
                      opacity: opening ? 1 : 0,
                      y: opening ? 0 : 14,
                      letterSpacing: opening ? "0.34em" : "0.18em",
                    }}
                    transition={{
                      duration: 1.6,
                      delay: 0.8,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                    className="relative font-serif font-light uppercase text-white text-[36px] sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl leading-none drop-shadow-[0_4px_28px_rgba(0,0,0,0.7)]"
                    style={{ letterSpacing: "0.34em" }}
                  >
                    AB&nbsp;Events
                  </motion.h1>
                </div>

                <motion.div
                  className="pointer-events-none absolute left-1/2 top-[62%] -translate-x-1/2 origin-top scale-y-[-1]"
                  animate={{
                    opacity: revealed ? 0.13 : 0,
                    y: revealed ? 0 : -8,
                  }}
                  transition={{ duration: 1.2, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                  style={{
                    WebkitMaskImage:
                      "linear-gradient(to bottom, rgba(0,0,0,0.42) 0%, rgba(0,0,0,0.12) 45%, transparent 88%)",
                    maskImage:
                      "linear-gradient(to bottom, rgba(0,0,0,0.42) 0%, rgba(0,0,0,0.12) 45%, transparent 88%)",
                    filter: "blur(2.2px)",
                  }}
                >
                  <div className="flex flex-col items-center">
                    <img
                      src={LOGO_URL}
                      alt=""
                      aria-hidden="true"
                      className="w-24 sm:w-28 md:w-32 lg:w-36 h-auto object-contain opacity-65"
                      draggable={false}
                    />
                    <div
                      className="font-serif font-light uppercase text-white/55 text-[32px] sm:text-[40px] md:text-[52px] lg:text-[68px] xl:text-[82px] leading-none"
                      style={{ letterSpacing: "0.28em" }}
                    >
                      AB&nbsp;Events
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ scaleX: 0, opacity: 0 }}
                  animate={{
                    scaleX: revealed ? 1 : 0,
                    opacity: revealed ? 1 : 0,
                  }}
                  transition={{ duration: 1, delay: 0.2 }}
                  className="w-16 md:w-24 h-px bg-[#d4af37] my-6 md:my-8 origin-center shadow-[0_0_10px_rgba(212,175,55,0.6)]"
                />

                <motion.p
                  initial={{ opacity: 0, y: 8 }}
                  animate={{
                    opacity: revealed ? 1 : 0,
                    y: revealed ? 0 : 8,
                  }}
                  transition={{ duration: 0.9, delay: 0.3 }}
                  className="text-[10px] sm:text-xs md:text-sm tracking-[0.55em] uppercase text-[#d4af37] italic font-medium drop-shadow-[0_2px_8px_rgba(0,0,0,0.55)]"
                >
                  A Legacy of Indian Wedding Grandeur
                </motion.p>
              </motion.div>
            </div>
          </div>

          {/* === Layer 1 — LEFT carved palace door === */}
          <motion.div
            data-testid="palace-door-left"
            initial={{ x: 0 }}
            animate={{ x: opening ? "-101%" : 0 }}
            transition={{ duration: 3.0, ease: [0.76, 0, 0.24, 1] }}
            className="absolute top-0 left-0 h-full w-1/2 z-20 overflow-hidden"
            style={doorBase}
          >
            <div
              className="absolute inset-y-0 left-0 w-8"
              style={{
                background:
                  "linear-gradient(90deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.18) 46%, rgba(0,0,0,0) 100%)",
              }}
            />
            <div
              className="absolute inset-y-0 right-0 w-5"
              style={{
                background:
                  "linear-gradient(90deg, rgba(255,230,160,0.16) 0%, rgba(255,230,160,0.02) 55%, rgba(0,0,0,0) 100%)",
                boxShadow: "4px 0 14px rgba(0,0,0,0.28)",
              }}
            />
            {/* Inner gold-bordered carved panel */}
            <div className="absolute inset-6 md:inset-10 border border-[#d4af37]/55 rounded-t-[120px] md:rounded-t-[180px] pointer-events-none">
              <div className="absolute inset-3 border border-[#d4af37]/35 rounded-t-[110px] md:rounded-t-[170px]" />
            </div>

            {/* Filigree on door face */}
            <Filigree side="left" />

            {/* Brass door knob */}
            <div
              className="absolute right-4 md:right-6 top-1/2 -translate-y-1/2 w-3 h-3 md:w-4 md:h-4 rounded-full"
              style={{
                background:
                  "radial-gradient(circle at 30% 30%, #ffe28c 0%, #d4af37 45%, #6b4a14 100%)",
                boxShadow:
                  "0 0 12px rgba(212,175,55,0.65), inset 0 0 4px rgba(0,0,0,0.5)",
              }}
            />

            {/* Glowing seam on the closing edge */}
            <div
              className="absolute top-0 right-0 h-full w-px"
              style={{
                background:
                  "linear-gradient(to bottom, rgba(212,175,55,0) 0%, rgba(212,175,55,0.6) 25%, rgba(255,200,90,0.95) 50%, rgba(212,175,55,0.6) 75%, rgba(212,175,55,0) 100%)",
                boxShadow: "0 0 18px rgba(212,175,55,0.65)",
              }}
            />
          </motion.div>

          {/* === Layer 1 — RIGHT carved palace door === */}
          <motion.div
            data-testid="palace-door-right"
            initial={{ x: 0 }}
            animate={{ x: opening ? "101%" : 0 }}
            transition={{ duration: 3.0, ease: [0.76, 0, 0.24, 1] }}
            className="absolute top-0 right-0 h-full w-1/2 z-20 overflow-hidden"
            style={doorBase}
          >
            <div
              className="absolute inset-y-0 right-0 w-8"
              style={{
                background:
                  "linear-gradient(270deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.18) 46%, rgba(0,0,0,0) 100%)",
              }}
            />
            <div
              className="absolute inset-y-0 left-0 w-5"
              style={{
                background:
                  "linear-gradient(270deg, rgba(255,230,160,0.16) 0%, rgba(255,230,160,0.02) 55%, rgba(0,0,0,0) 100%)",
                boxShadow: "-4px 0 14px rgba(0,0,0,0.28)",
              }}
            />
            <div className="absolute inset-6 md:inset-10 border border-[#d4af37]/55 rounded-t-[120px] md:rounded-t-[180px] pointer-events-none">
              <div className="absolute inset-3 border border-[#d4af37]/35 rounded-t-[110px] md:rounded-t-[170px]" />
            </div>

            <Filigree side="right" />

            <div
              className="absolute left-4 md:left-6 top-1/2 -translate-y-1/2 w-3 h-3 md:w-4 md:h-4 rounded-full"
              style={{
                background:
                  "radial-gradient(circle at 30% 30%, #ffe28c 0%, #d4af37 45%, #6b4a14 100%)",
                boxShadow:
                  "0 0 12px rgba(212,175,55,0.65), inset 0 0 4px rgba(0,0,0,0.5)",
              }}
            />

            <div
              className="absolute top-0 left-0 h-full w-px"
              style={{
                background:
                  "linear-gradient(to bottom, rgba(212,175,55,0) 0%, rgba(212,175,55,0.6) 25%, rgba(255,200,90,0.95) 50%, rgba(212,175,55,0.6) 75%, rgba(212,175,55,0) 100%)",
                boxShadow: "0 0 18px rgba(212,175,55,0.65)",
              }}
            />
          </motion.div>

          {/* Floating gold dust during the closed phase */}
          <AnimatePresence>
            {phase === "closed" && (
              <motion.div
                key="dust"
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.6 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6 }}
                className="absolute inset-0 z-30 pointer-events-none"
              >
                {[...Array(profile.dustCount)].map((_, i) => (
                  <motion.span
                    key={i}
                    initial={{
                      opacity: 0,
                      x: `${10 + Math.random() * 80}vw`,
                      y: `${20 + Math.random() * 60}vh`,
                    }}
                    animate={{
                      opacity: [0, 0.85, 0],
                      y: `${10 + Math.random() * 30}vh`,
                    }}
                    transition={{
                      duration: 2 + Math.random() * 2,
                      repeat: Infinity,
                      delay: Math.random() * 1.2,
                      ease: "easeOut",
                    }}
                    className="absolute w-[3px] h-[3px] rounded-full bg-[#ffd884]"
                    style={{ boxShadow: "0 0 8px rgba(255,216,132,0.95)" }}
                  />
                ))}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Tiny overline visible only while doors are closed */}
          <AnimatePresence>
            {phase === "closed" && !profile.lite && (
              <motion.div
                key="overline"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.7 }}
                className="absolute bottom-10 left-0 right-0 z-40 text-center text-[9px] md:text-[10px] tracking-[0.55em] uppercase text-[#d4af37]/85"
              >
                ⌁ The doors are about to open ⌁
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default IntroLoader;
